/**
 * HeadingCone
 *
 * Shared heading wedge for live-location markers: a CSS triangle pointing in the
 * reported device heading, rendered behind the marker dot/avatar. Used by both
 * the local user's own marker and remote members' markers — the only difference
 * is the fill color, passed via the `color` prop (a CSS value/var) and surfaced
 * to CSS as the `--cone-color` custom property.
 *
 * Renders nothing when no finite heading is available.
 *
 * @module components/map/UserLocationLayer/parts/HeadingCone
 */
import type { CSSProperties } from 'react';
import { memo, useMemo } from 'react';

import {
  USER_LOCATION_HALO_SIZE_PX,
  USER_LOCATION_HEADING_CONE_ANGLE_DEG,
} from '@/constants';

import styles from './HeadingCone.module.css';

export interface HeadingConeProps {
  /** Device heading in degrees (0–360, clockwise from north); absent → hidden. */
  heading: number | undefined;
  /** Fill color for the cone — a CSS value/var (theme accent or member color). */
  color: string;
}

/**
 * Renders the heading wedge, or `null` when the heading is not a finite number.
 * The cone geometry (`--halo-size`, `--cone-angle`) stays sourced from
 * `@/constants`; the rotation (`--heading-deg`) and fill (`--cone-color`) are
 * driven by props.
 */
export const HeadingCone = memo(function HeadingCone({
  heading,
  color,
}: HeadingConeProps) {
  const coneStyle = useMemo<CSSProperties>(
    () =>
      ({
        '--halo-size': `${USER_LOCATION_HALO_SIZE_PX}px`,
        '--cone-angle': `${USER_LOCATION_HEADING_CONE_ANGLE_DEG}deg`,
        '--heading-deg': `${heading}deg`,
        '--cone-color': color,
      }) as CSSProperties,
    [heading, color]
  );

  if (!Number.isFinite(heading)) {
    return null;
  }

  return <span className={styles.cone} style={coneStyle} aria-hidden="true" />;
});
